/**
 * Sysadmin agent lifecycle action bar — port (PR phase4/sysadmin-agent-builder-port).
 *
 * Extracted from `routes/sysadmin/catalog.agents.$id.tsx` (the divergent
 * 9-tab shell that the parity audit flagged for replacement). These
 * controls — Publish / Unpublish (deprecated|retired) / Re-publish /
 * Restore-to-Draft / Delete / Test Bench — are SYSADMIN-ONLY affordances.
 * The tenant builder at `routes/agents/builder.tsx` doesn't get them.
 *
 * "View as Tenant" was dropped 2026-05-11 per founder direction (PR #337
 * H1): sysadmin is the canonical author for system agents/tools/functions
 * — there is no tenant-specific render that differs from sysadmin's view.
 * If preset dials later let tenants customize what they see, we can revisit.
 *
 * Mounting pattern: render BETWEEN the page header and the conversation/
 * canvas split so the bar sits above the canvas without interfering with
 * the conversation pane on the left.
 *
 * Publish is gated by the API's exhaustive dangling-reference validator
 * — the categorized 422 payload is surfaced via toast so sysadmin sees
 * which model / prompt / tools / functions / skills / library / workflows
 * blocked the publish.
 *
 * Behavior is byte-for-byte preserved from the original
 * `SysadminCatalogAgentDetailPage` (PR #331) — no business-logic change.
 */

import { useState } from 'react';
import {
  ShieldCheck,
  Loader2,
  Trash2,
  Beaker,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  useDeleteCatalogAgent,
  usePublishCatalogAgent,
  useUnpublishCatalogAgent,
  useTestBenchCatalogAgent,
  type CatalogAgentDetail,
  type AgentStatus,
} from '@/lib/use-sysadmin-catalog-agents';

// ─── Public helpers (exported for tests) ──────────────────────────────────

export function statusPillClass(status: AgentStatus): string {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'draft':
      return 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30';
    case 'deprecated':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'retired':
      return 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-400 border-zinc-500/30';
  }
}

/**
 * Format a categorized DANGLING_REFERENCES error payload from the
 * publish endpoint into a human-readable list of failure causes.
 *
 * Exported for test coverage — the original page had this logic
 * inlined inside handlePublish, which made it untestable.
 */
export function formatDanglingReferencesError(
  details: Record<string, unknown> | undefined,
): string {
  if (!details) return 'unknown publish error';
  const parts: string[] = [];
  if (details.missingModelId) parts.push('model is missing');
  if (details.inactiveModelId) parts.push('model is inactive');
  if (details.missingSystemPromptId) parts.push('system prompt template is missing');
  if (details.inactiveSystemPromptId) parts.push('system prompt template is inactive');
  const danglingToolSlugs = details.danglingToolSlugs as unknown[] | undefined;
  if (danglingToolSlugs?.length)
    parts.push(`${danglingToolSlugs.length} dangling tool(s)`);
  const unpublishedToolSlugs = details.unpublishedToolSlugs as unknown[] | undefined;
  if (unpublishedToolSlugs?.length)
    parts.push(`${unpublishedToolSlugs.length} unpublished tool(s)`);
  const danglingFunctionKeys = details.danglingFunctionKeys as unknown[] | undefined;
  if (danglingFunctionKeys?.length)
    parts.push(`${danglingFunctionKeys.length} dangling function(s)`);
  const unpublishedFunctionKeys = details.unpublishedFunctionKeys as unknown[] | undefined;
  if (unpublishedFunctionKeys?.length)
    parts.push(`${unpublishedFunctionKeys.length} unpublished function(s)`);
  const danglingSkillSlugs = details.danglingSkillSlugs as unknown[] | undefined;
  if (danglingSkillSlugs?.length)
    parts.push(`${danglingSkillSlugs.length} dangling skill(s)`);
  const unpublishedSkillSlugs = details.unpublishedSkillSlugs as unknown[] | undefined;
  if (unpublishedSkillSlugs?.length)
    parts.push(`${unpublishedSkillSlugs.length} unpublished skill(s)`);
  const danglingLibraryRefs = details.danglingLibraryRefs as unknown[] | undefined;
  if (danglingLibraryRefs?.length)
    parts.push(`${danglingLibraryRefs.length} dangling library ref(s)`);
  const unpublishedLibraryRefs = details.unpublishedLibraryRefs as unknown[] | undefined;
  if (unpublishedLibraryRefs?.length)
    parts.push(`${unpublishedLibraryRefs.length} unpublished library ref(s)`);
  const danglingWorkflowSlugs = details.danglingWorkflowSlugs as unknown[] | undefined;
  if (danglingWorkflowSlugs?.length)
    parts.push(`${danglingWorkflowSlugs.length} dangling workflow(s)`);
  return parts.join(' · ') || 'unknown publish error';
}

/**
 * Decide which lifecycle buttons are visible based on the agent's
 * current status + clone count. Pure, deterministic — exported for tests.
 */
export interface LifecycleAffordances {
  canPublish: boolean;
  canRepublish: boolean;
  canDeprecate: boolean;
  canRetire: boolean;
  canRestoreToDraft: boolean;
  canDelete: boolean;
}

export function getLifecycleAffordances(
  status: AgentStatus,
  cloneCount: number,
): LifecycleAffordances {
  // CATALOG-MIGRATION-DESIGN §6.11 (PR-U1) dropped `deprecated`/`retired` from
  // the status enum and REMOVED the unpublish endpoints across all entities —
  // agent status is now binary (draft | published), with operational on/off via
  // `enabled` and soft-delete via `deleted_at`. The Deprecate / Retire /
  // Re-publish / Restore-to-Draft transitions no longer exist, and their buttons
  // called `POST /catalog/agents/:id/unpublish`, which now returns 410 Gone.
  // Surfacing them was a dead affordance (platform review 2026-06-09): the only
  // honest options are Publish (draft → published) and Delete (a draft). A
  // published agent is edited in place via PATCH (dependency-revalidated) — major
  // changes mint a new slug (PQ-2), they are not unpublished.
  return {
    canPublish: status === 'draft',
    canRepublish: false,
    canDeprecate: false,
    canRetire: false,
    canRestoreToDraft: false,
    canDelete: status === 'draft' && cloneCount === 0,
  };
}

// ─── Action bar ───────────────────────────────────────────────────────────

export interface AgentLifecycleActionBarProps {
  agent: CatalogAgentDetail;
  /**
   * Optional override for the back-navigation target. Defaults to
   * `/sysadmin/catalog/agents` so deletes return to the catalog list.
   */
  onDeleted?: () => void;
}

export function AgentLifecycleActionBar({
  agent,
  onDeleted,
}: AgentLifecycleActionBarProps) {
  const navigate = useNavigate();
  const publish = usePublishCatalogAgent();
  const unpublish = useUnpublishCatalogAgent();
  const del = useDeleteCatalogAgent();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState<
    'deprecated' | 'retired' | 'draft' | null
  >(null);
  const [unpublishReason, setUnpublishReason] = useState('');
  const [showTestBench, setShowTestBench] = useState(false);

  const affordances = getLifecycleAffordances(agent.status, agent.cloneCount);

  async function handlePublish() {
    try {
      await publish.mutateAsync({ id: agent.id });
      toast.success(`Agent "${agent.slug}" published.`);
    } catch (err: unknown) {
      const e = err as { body?: { error?: { code?: string; details?: Record<string, unknown>; message?: string } }; message?: string };
      const code = e?.body?.error?.code;
      const details = e?.body?.error?.details;
      if (code === 'DANGLING_REFERENCES') {
        toast.error(`Cannot publish: ${formatDanglingReferencesError(details)}`, {
          duration: 12000,
        });
      } else {
        toast.error(
          `Publish failed: ${e?.body?.error?.message ?? e?.message ?? 'unknown error'}`,
        );
      }
    }
  }

  async function handleUnpublish() {
    if (!confirmUnpublish || !unpublishReason.trim()) return;
    try {
      await unpublish.mutateAsync({
        id: agent.id,
        reason: unpublishReason,
        nextStatus: confirmUnpublish,
      });
      toast.success(`Agent moved to ${confirmUnpublish}.`);
      setConfirmUnpublish(null);
      setUnpublishReason('');
    } catch (err: unknown) {
      const e = err as { body?: { error?: { message?: string } }; message?: string };
      toast.error(
        `Unpublish failed: ${e?.body?.error?.message ?? e?.message ?? 'unknown error'}`,
      );
    }
  }

  async function handleDelete() {
    try {
      await del.mutateAsync({ id: agent.id });
      toast.success(`Agent "${agent.slug}" deleted.`);
      if (onDeleted) {
        onDeleted();
      } else {
        navigate({ to: '/sysadmin/catalog/agents' });
      }
    } catch (err: unknown) {
      const e = err as { body?: { error?: { message?: string } }; message?: string };
      toast.error(
        `Delete failed: ${e?.body?.error?.message ?? e?.message ?? 'unknown error'}`,
      );
    }
  }

  return (
    <div className="border-y border-border/40 bg-muted/20 px-4 py-2 flex items-center gap-2 flex-wrap shrink-0">
      <Badge
        variant="outline"
        className={cn('text-[10px] capitalize border', statusPillClass(agent.status))}
        title={`Lifecycle status: ${agent.status}`}
      >
        {agent.status}
      </Badge>
      <span className="text-[10px] text-muted-foreground font-mono" title="Slug">
        {agent.slug}
      </span>
      <span className="text-[10px] text-muted-foreground" title="Version">
        v{agent.version}
      </span>
      <span className="text-[10px] text-muted-foreground" title="Tenant clones">
        {agent.cloneCount} clone{agent.cloneCount === 1 ? '' : 's'}
      </span>

      <div className="ml-auto flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-7 text-xs"
          onClick={() => setShowTestBench(true)}
          title="Run a test invocation in the sysadmin sandbox"
        >
          <Beaker className="h-3.5 w-3.5" />
          Test Bench
        </Button>

        {affordances.canPublish && (
          <Button
            size="sm"
            className="gap-1.5 h-7 text-xs"
            onClick={handlePublish}
            disabled={publish.isPending}
            title="Validate composition + publish to all tenants"
          >
            {publish.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5" />
            )}
            Publish
          </Button>
        )}

        {affordances.canRepublish && (
          <Button
            size="sm"
            className="gap-1.5 h-7 text-xs"
            onClick={handlePublish}
            disabled={publish.isPending}
            title="Re-publish from deprecated"
          >
            {publish.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5" />
            )}
            Re-publish
          </Button>
        )}

        {affordances.canDeprecate && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-7 text-xs"
            onClick={() => setConfirmUnpublish('deprecated')}
            title="Move to deprecated — existing clones survive, no new clones"
          >
            Deprecate
          </Button>
        )}

        {affordances.canRetire && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-7 text-xs"
            onClick={() => setConfirmUnpublish('retired')}
            title="Retire — existing clones lose source linkage"
          >
            Retire
          </Button>
        )}

        {affordances.canRestoreToDraft && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-7 text-xs"
            onClick={() => setConfirmUnpublish('draft')}
            title="Restore to draft — only allowed when no clones exist"
          >
            Restore to Draft
          </Button>
        )}

        {affordances.canDelete && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-7 text-xs text-destructive hover:text-destructive"
            onClick={() => setConfirmDelete(true)}
            disabled={agent.cloneCount > 0}
            title={
              agent.cloneCount > 0
                ? 'Cannot delete: tenant clones exist'
                : 'Delete agent'
            }
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </div>

      {/* Test bench drawer */}
      <TestBenchSheet
        open={showTestBench}
        onOpenChange={setShowTestBench}
        agent={agent}
      />

      {/* Unpublish dialog */}
      <AlertDialog
        open={confirmUnpublish !== null}
        onOpenChange={(o) => !o && setConfirmUnpublish(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmUnpublish === 'draft'
                ? 'Restore to draft?'
                : `Move agent to ${confirmUnpublish}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmUnpublish === 'deprecated'
                ? 'Existing tenant clones survive but no new tenants can clone this agent.'
                : confirmUnpublish === 'retired'
                  ? 'This is a hard stop. Existing clones lose their link back to this agent.'
                  : 'Restoring requires zero tenant clones. This action is reversible.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="unpub-reason" className="text-xs">
              Reason (required)
            </Label>
            <Textarea
              id="unpub-reason"
              placeholder="Why is this agent moving status?"
              value={unpublishReason}
              onChange={(e) => setUnpublishReason(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setConfirmUnpublish(null);
                setUnpublishReason('');
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublish}
              disabled={!unpublishReason.trim() || unpublish.isPending}
            >
              {unpublish.isPending ? 'Working…' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete dialog */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes the agent (sets is_active=false). It can
              be restored from the sysadmin audit log if needed. Tenant
              clones must be revoked first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Test bench drawer ────────────────────────────────────────────────────

function TestBenchSheet({
  open,
  onOpenChange,
  agent,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  agent: CatalogAgentDetail;
}) {
  const [query, setQuery] = useState('');
  const tb = useTestBenchCatalogAgent();
  const [result, setResult] = useState<
    Awaited<ReturnType<typeof tb.mutateAsync>> | null
  >(null);

  async function run() {
    if (!query.trim()) return;
    try {
      const r = await tb.mutateAsync({ id: agent.id, query });
      setResult(r);
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(`Test bench failed: ${e?.message ?? 'unknown'}`);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Beaker className="h-5 w-5" />
            Test bench — {agent.displayName}
          </SheetTitle>
          <SheetDescription>
            Sysadmin sandbox invocation. Does NOT pollute tenant audit
            logs. Audited under{' '}
            <span className="font-mono text-xs">
              system_agent.test_bench_invoked
            </span>{' '}
            in the platform audit chain.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor="tb-query">Query</Label>
            <Textarea
              id="tb-query"
              rows={5}
              placeholder="What would you ask this agent?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mt-1.5"
            />
          </div>

          <Button
            onClick={run}
            disabled={!query.trim() || tb.isPending}
            className="gap-1.5"
          >
            {tb.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running…
              </>
            ) : (
              <>
                <Beaker className="h-3.5 w-3.5" /> Run
              </>
            )}
          </Button>

          {result && (() => {
            // #619 — The test-bench endpoint currently returns
            // `status: 'stub_completed'` because there is no real
            // sysadmin-scoped agent runner yet. The previous UI rendered
            // every result as an emerald-success card with a check icon,
            // which made stub output look like a passing quality run.
            // We now branch on the status: real runs keep the emerald
            // success treatment, stub runs render as an amber warning
            // card with an explicit "no real model invocation" label.
            const isStub = result.status === 'stub_completed';
            return (
            <Card
              className={cn(
                isStub
                  ? 'border-amber-500/40 bg-amber-50/30 dark:bg-amber-950/10'
                  : 'border-emerald-500/30',
              )}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {isStub ? (
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  )}
                  <span className={cn(isStub && 'text-amber-700 dark:text-amber-400')}>
                    {result.status}
                  </span>
                  {isStub && (
                    <Badge
                      variant="outline"
                      className="text-[10px] h-4 px-1.5 border-amber-500/50 text-amber-700 dark:text-amber-400 ml-1"
                    >
                      stub · no real model run
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="font-mono text-xs">
                  {result.invocationId} · {result.latencyMs}ms · in
                  {result.tokens.input}/out{result.tokens.output}
                </CardDescription>
                {isStub && (
                  <CardDescription className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    This response is a deterministic stub for plumbing
                    only. The agent runner is not wired yet — do NOT
                    treat this as a successful golden-set/quality pass.
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {result.warnings.length > 0 && (
                  <div className="text-xs space-y-1">
                    <div className="text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Warnings ({result.warnings.length})
                    </div>
                    <ul className="list-disc pl-5 text-amber-700 dark:text-amber-300">
                      {result.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <pre className="text-xs whitespace-pre-wrap bg-muted/30 p-3 rounded-md border font-sans">
                  {result.response}
                </pre>
              </CardContent>
            </Card>
            );
          })()}
        </div>
      </SheetContent>
    </Sheet>
  );
}

